import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calendar,
  Mail,
  Bell,
  Users,
  Plane,
  Plus,
  Trash2,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertTriangle,
  User,
  Edit3,
  XCircle,
  RotateCcw,
  Save
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { useLifely } from '../context/LifelyContext';
import ContactSelector from '../components/ContactSelector';
import { toast } from 'sonner';

// Section header component
const SectionHeader = ({ icon: Icon, title, subtitle, section, expandedSections, toggleSection, iconBgClass, iconClass }) => (
  <button
    onClick={() => toggleSection(section)}
    className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors rounded-t-lg"
  >
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconBgClass || 'bg-primary/10'}`}>
        <Icon className={`w-5 h-5 ${iconClass || 'text-primary'}`} />
      </div>
      <div className="text-left">
        <h3 className="font-medium">{title}</h3>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
    {expandedSections[section] ? (
      <ChevronUp className="w-5 h-5 text-muted-foreground" />
    ) : (
      <ChevronDown className="w-5 h-5 text-muted-foreground" />
    )}
  </button>
);

const INTERVAL_UNITS = [
  { value: 'days', label: 'Days' },
  { value: 'weeks', label: 'Weeks' },
  { value: 'months', label: 'Months' },
];

const ESCALATION_METHODS = [
  { value: 'email', label: 'Email only' },
  { value: 'sms', label: 'SMS only' },
  { value: 'both', label: 'Email + SMS' },
  { value: 'trusted-contact', label: 'Trusted contact check' },
];

const ContinuitySetup = () => {
  const navigate = useNavigate();
  const { 
    user, 
    shareSettings, 
    getContactById, 
    saveContinuitySettings, 
    updateContinuityConfig,
    continuityConfig,
    fetchContinuity,
    fetchSharing,
    fetchContacts,
    isAuthenticated,
    loading,
    userContactPrefs,
  } = useLifely();
  
  const [dataLoaded, setDataLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Fetch data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([
            fetchContinuity(),
            fetchSharing(),
            fetchContacts()
          ]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load continuity data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchContinuity, fetchSharing, fetchContacts]);

  // Initialize state from existing config
  const config = continuityConfig || {};
  const isCurrentlyConfigured = config.isConfigured || false;
  
  // Check-in frequency
  const [checkInInterval, setCheckInInterval] = useState(config.checkInFrequency || config.checkInInterval || 0);
  const [checkInUnit, setCheckInUnit] = useState(config.checkInUnit || 'months');
  
  // Contact methods
  const [alternativeEmail, setAlternativeEmail] = useState(config.alternativeEmail || '');
  const [phoneNumber, setPhoneNumber] = useState(config.phoneNumber || '');
  const [useEmail, setUseEmail] = useState(config.useEmail || false);
  const [useSms, setUseSms] = useState(config.useSms || false);

  // Update state when config changes (initial load)
  useEffect(() => {
    if (continuityConfig && dataLoaded) {
      setCheckInInterval(continuityConfig.checkInFrequency || continuityConfig.checkInInterval || 0);
      setCheckInUnit(continuityConfig.checkInUnit || 'months');
      setAlternativeEmail(continuityConfig.alternativeEmail || '');
      setPhoneNumber(continuityConfig.phoneNumber || '');
      setUseEmail(continuityConfig.useEmail || false);
      setUseSms(continuityConfig.useSms || false);
      setHasUnsavedChanges(false);
    }
  }, [continuityConfig, dataLoaded]);
  
  // Escalation steps
  const [escalationSteps, setEscalationSteps] = useState([
    { id: 1, method: 'email', waitDays: 3, contactId: '' },
    { id: 2, method: 'both', waitDays: 2, contactId: '' },
    { id: 3, method: 'both', waitDays: 2, contactId: '' },
  ]);
  
  // Pause settings
  const [showPauseSection, setShowPauseSection] = useState(false);
  const [pauseUntil, setPauseUntil] = useState('');
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    frequency: true,
    contact: true,
    escalation: false,
    nominees: true,
  });
  
  // Confirmation
  const [confirmed, setConfirmed] = useState(false);

  // Determine if current form values represent a "configured" state
  const isFormConfigured = checkInInterval > 0 && (useEmail || useSms);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Track changes
  const markChanged = () => {
    setHasUnsavedChanges(true);
  };

  const addEscalationStep = () => {
    if (escalationSteps.length < 5) {
      setEscalationSteps([
        ...escalationSteps,
        { id: Date.now(), method: 'both', waitDays: 2, contactId: '' }
      ]);
    }
  };

  const removeEscalationStep = (stepId) => {
    if (escalationSteps.length > 1) {
      setEscalationSteps(escalationSteps.filter(s => s.id !== stepId));
    }
  };

  const updateEscalationStep = (stepId, field, value) => {
    setEscalationSteps(escalationSteps.map(s => 
      s.id === stepId ? { ...s, [field]: value } : s
    ));
  };

  const getTotalGracePeriod = () => {
    return escalationSteps.reduce((sum, step) => sum + step.waitDays, 0);
  };

  // Reset form to defaults
  const handleResetToDefaults = () => {
    setCheckInInterval(0);
    setCheckInUnit('months');
    setUseEmail(false);
    setUseSms(false);
    setAlternativeEmail('');
    setPhoneNumber('');
    setConfirmed(false);
    setHasUnsavedChanges(true);
    setShowResetDialog(false);
    toast.info('Settings reset to defaults. Click "Save" to apply changes.');
  };

  // Save settings (works for both activate and deactivate)
  const handleSave = async () => {
    // If trying to activate, validate
    if (isFormConfigured && !confirmed) {
      toast.error('Please confirm the acknowledgment before activating');
      return;
    }
    
    try {
      setSaving(true);
      
      // Determine if this should be marked as configured
      const shouldBeConfigured = checkInInterval > 0 && (useEmail || useSms);
      
      // Update continuity config
      await updateContinuityConfig({
        checkInFrequency: checkInInterval,
        checkInInterval,
        checkInUnit,
        alternativeEmail,
        phoneNumber,
        useEmail,
        useSms,
        isConfigured: shouldBeConfigured,
        isActive: shouldBeConfigured,
      });
      
      if (shouldBeConfigured) {
        // Activate continuity
        await saveContinuitySettings();
        toast.success('Continuity activated! Check-ins are now enabled.');
      } else {
        toast.success('Continuity deactivated. Settings saved.');
      }
      
      setHasUnsavedChanges(false);
      navigate('/continuity');
    } catch (err) {
      toast.error('Failed to save settings');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Nominees from sharing settings
  const nominees = shareSettings?.filter(s => s.shareTrigger === 'continuity' || s.shareTrigger === 'both') || [];

  // Show loading state
  if (loading.continuity && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="continuity-setup-page">
      <Link 
        to="/continuity" 
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Continuity
      </Link>

      <div>
        <h1 className="text-2xl font-heading font-semibold">Continuity Setup</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Lifely can check in with you periodically. If you don&apos;t respond, your chosen people will receive the information you&apos;ve configured.
        </p>
      </div>

      {/* Status Banner */}
      <Card className={`border-2 ${isFormConfigured ? 'border-green-500/30 bg-green-500/5' : 'border-blue-500/30 bg-blue-500/5'}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isFormConfigured ? (
                <>
                  <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">
                      {hasUnsavedChanges ? 'Will be Active (unsaved)' : 'Continuity is Active'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Check-ins every {checkInInterval} {checkInUnit}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-blue-700 dark:text-blue-400">
                      {hasUnsavedChanges ? 'Will be Deactivated (unsaved)' : 'Continuity Not Configured'}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {hasUnsavedChanges ? 'Click Save to apply changes' : 'Complete the setup below to activate'}
                    </p>
                  </div>
                </>
              )}
            </div>
            {hasUnsavedChanges && (
              <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300">
                Unsaved Changes
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 1: Check-in Frequency */}
      <Card className={`border overflow-hidden ${checkInInterval <= 0 ? 'border-blue-200 dark:border-blue-800' : 'border-border'}`}>
        <SectionHeader 
          icon={Calendar} 
          title="How often should we check in?" 
          subtitle={checkInInterval > 0 ? `Every ${checkInInterval} ${checkInUnit}` : 'Not set'}
          section="frequency"
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          iconBgClass={checkInInterval > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}
          iconClass={checkInInterval > 0 ? 'text-green-600' : 'text-blue-600'}
        />
        {expandedSections.frequency && (
          <CardContent className="pt-0 pb-6 px-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className="text-sm">Every</span>
                <Input
                  type="number"
                  min="0"
                  max="365"
                  value={checkInInterval}
                  onChange={(e) => { setCheckInInterval(parseInt(e.target.value) || 0); markChanged(); }}
                  className="w-20 h-9"
                  placeholder="0"
                />
                <Select value={checkInUnit} onValueChange={(v) => { setCheckInUnit(v); markChanged(); }}>
                  <SelectTrigger className="w-28 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {checkInInterval === 0 && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Set a value greater than 0 to enable check-ins
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Recommended: 2 weeks for active users, 1 month for occasional check-ins
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Step 2: Contact Methods */}
      <Card className={`border overflow-hidden ${!useEmail && !useSms ? 'border-blue-200 dark:border-blue-800' : 'border-border'}`}>
        <SectionHeader 
          icon={Mail} 
          title="How should we reach you?" 
          subtitle={useEmail || useSms ? `Via ${[useEmail && 'Email', useSms && 'SMS'].filter(Boolean).join(' & ')}` : 'Not selected'}
          section="contact"
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          iconBgClass={useEmail || useSms ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}
          iconClass={useEmail || useSms ? 'text-green-600' : 'text-blue-600'}
        />
        {expandedSections.contact && (
          <CardContent className="pt-0 pb-6 px-6 space-y-4">
            {/* Link to My Contact Details */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Your Contact Details</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Check-in reminders will be sent using your contact details from the Contacts page.
                  </p>
                </div>
              </div>
              
              <div className="pl-11 space-y-2">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Primary Email</p>
                    <p className="font-medium">{user?.email || '—'}</p>
                  </div>
                  {userContactPrefs?.alternativeEmail && (
                    <div>
                      <p className="text-xs text-muted-foreground">Alternative Email</p>
                      <p className="font-medium">{userContactPrefs.alternativeEmail}</p>
                    </div>
                  )}
                  {userContactPrefs?.phone && (
                    <div>
                      <p className="text-xs text-muted-foreground">Phone</p>
                      <p className="font-medium">{userContactPrefs.phone}</p>
                    </div>
                  )}
                </div>
                
                <Button variant="outline" size="sm" asChild className="mt-2">
                  <Link to="/contacts">
                    <Edit3 className="w-3 h-3 mr-1" /> Edit My Contact Details
                  </Link>
                </Button>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label className="text-xs text-muted-foreground">Send reminders via</Label>
              <div className="flex gap-4">
                <div className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${useEmail ? 'border-green-500/50 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}>
                  <Checkbox id="use-email" checked={useEmail} onCheckedChange={(v) => { setUseEmail(v); markChanged(); }} />
                  <Label htmlFor="use-email" className="text-sm font-normal cursor-pointer">Email</Label>
                </div>
                <div className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${useSms ? 'border-green-500/50 bg-green-50 dark:bg-green-900/20' : 'border-border'}`}>
                  <Checkbox 
                    id="use-sms" 
                    checked={useSms} 
                    onCheckedChange={(v) => { setUseSms(v); markChanged(); }} 
                    disabled={!userContactPrefs?.phone} 
                  />
                  <Label htmlFor="use-sms" className={`text-sm font-normal cursor-pointer ${!userContactPrefs?.phone ? 'text-muted-foreground' : ''}`}>
                    SMS {!userContactPrefs?.phone && '(add phone first)'}
                  </Label>
                </div>
              </div>
              {!useEmail && !useSms && (
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  Select at least one contact method to activate continuity
                </p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Step 3: Escalation Steps */}
      <Card className="border border-border overflow-hidden">
        <SectionHeader 
          icon={Bell} 
          title="What if you miss a check-in?" 
          subtitle={`Currently: ${escalationSteps.length} reminders over ~${getTotalGracePeriod()} days`}
          section="escalation"
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        {expandedSections.escalation && (
          <CardContent className="pt-0 pb-6 px-6 space-y-4">
            <p className="text-xs text-muted-foreground">
              Configure what happens if you miss a check-in. Add reminder steps or a trusted contact verification.
            </p>
            <div className="space-y-3">
              {escalationSteps.map((step, index) => (
                <div key={step.id} className="p-3 rounded-lg border border-border bg-muted/30 space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium w-16">Step {index + 1}</span>
                    <Select 
                      value={step.method} 
                      onValueChange={(v) => updateEscalationStep(step.id, 'method', v)}
                    >
                      <SelectTrigger className="w-44 h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ESCALATION_METHODS.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm">then wait</span>
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={step.waitDays}
                      onChange={(e) => updateEscalationStep(step.id, 'waitDays', parseInt(e.target.value) || 1)}
                      className="w-16 h-8"
                    />
                    <span className="text-sm">days</span>
                    {escalationSteps.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0 text-destructive"
                        onClick={() => removeEscalationStep(step.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  {step.method === 'trusted-contact' && (
                    <div className="pl-16 space-y-2">
                      <Label className="text-xs text-muted-foreground">Select trusted contact to verify</Label>
                      <ContactSelector
                        value={step.contactId}
                        onSelect={(contactId) => updateEscalationStep(step.id, 'contactId', contactId)}
                        placeholder="Select someone who can check on you..."
                      />
                      {step.contactId && (() => {
                        const contact = getContactById(step.contactId);
                        if (!contact?.email) {
                          return (
                            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                                <p className="text-xs text-amber-800 dark:text-amber-200">
                                  This contact has no email. Please update their details in the Contacts tab.
                                </p>
                              </div>
                            </div>
                          );
                        }
                        return <p className="text-xs text-muted-foreground">Will email: {contact.email}</p>;
                      })()}
                    </div>
                  )}
                </div>
              ))}
              
              <div className="p-3 rounded-lg border border-primary/30 bg-primary/5">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Final Step: Notify nominees</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Send configured access to all nominees (cannot be removed)
                </p>
              </div>
              
              {escalationSteps.length < 5 && (
                <Button variant="outline" size="sm" onClick={addEscalationStep}>
                  <Plus className="w-4 h-4 mr-1" /> Add Step
                </Button>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Step 4: Nominees */}
      <Card className={`border overflow-hidden ${nominees.length === 0 ? 'border-blue-200 dark:border-blue-800' : 'border-border'}`}>
        <SectionHeader 
          icon={Users} 
          title="Who should be notified?" 
          subtitle={nominees.length > 0 ? `${nominees.length} nominee(s) configured` : 'No one configured'}
          section="nominees"
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          iconBgClass={nominees.length > 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}
          iconClass={nominees.length > 0 ? 'text-green-600' : 'text-blue-600'}
        />
        {expandedSections.nominees && (
          <CardContent className="pt-0 pb-6 px-6 space-y-4">
            {nominees.length === 0 ? (
              <div className="text-center py-6">
                <Users className="w-10 h-10 mx-auto text-blue-400 mb-3" />
                <p className="text-sm text-muted-foreground">
                  No nominees configured yet. Add sharing rules to specify who should receive your information.
                </p>
                <Button variant="outline" size="sm" className="mt-4" asChild>
                  <Link to="/sharing" state={{ from: 'continuity' }}>
                    <Plus className="w-4 h-4 mr-1" /> Configure in Sharing
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {nominees.map((share) => (
                  <div key={share.id} className="flex items-center justify-between p-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
                    <div>
                      <p className="font-medium text-sm">
                        {share.contactName || getContactById(share.contactId)?.name || 'Unknown contact'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {share.shareType === 'all_maps' ? 'All Maps' : `${share.selectedMaps?.length || 0} Maps`}
                        {share.includeWillStatus && ' + Will Status'}
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900/30">
                      {share.shareTrigger}
                    </Badge>
                  </div>
                ))}
                <Button variant="outline" size="sm" asChild>
                  <Link to="/sharing" state={{ from: 'continuity' }}>Edit in Sharing</Link>
                </Button>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Pause/Travelling Section */}
      <Card className="border border-border overflow-hidden">
        <button
          onClick={() => setShowPauseSection(!showPauseSection)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/30"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Plane className="w-5 h-5 text-amber-600" />
            </div>
            <div className="text-left">
              <h3 className="font-medium">Travelling or unavailable?</h3>
              <p className="text-sm text-muted-foreground">Pause check-ins temporarily</p>
            </div>
          </div>
          {showPauseSection ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        {showPauseSection && (
          <CardContent className="pt-0 pb-6 px-6 space-y-4">
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                If you know you&apos;ll be out of contact, pause check-ins to avoid triggering escalation.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Pause until</Label>
              <Input
                type="date"
                value={pauseUntil}
                onChange={(e) => setPauseUntil(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="h-9 w-48"
              />
            </div>
            {pauseUntil && (
              <Button variant="outline" onClick={() => toast.success('Check-ins paused')}>
                Pause Check-ins
              </Button>
            )}
          </CardContent>
        )}
      </Card>

      {/* Confirmation & Save */}
      <Card className="border border-border">
        <CardContent className="p-6 space-y-4">
          {/* Only show confirmation checkbox if activating */}
          {isFormConfigured && (
            <div className="flex items-start gap-3 p-4 rounded-lg border border-border bg-muted/30">
              <Checkbox
                id="confirm-understanding"
                checked={confirmed}
                onCheckedChange={setConfirmed}
                className="mt-0.5"
              />
              <Label htmlFor="confirm-understanding" className="text-sm font-normal cursor-pointer">
                I understand that if I don&apos;t respond to check-ins, my nominees will receive the configured information. 
                This does not replace a legal will.
              </Label>
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            {/* Left side: Reset button (only if currently configured) */}
            {isCurrentlyConfigured ? (
              <Button 
                variant="outline" 
                className="text-destructive border-destructive/50 hover:bg-destructive/10"
                onClick={() => setShowResetDialog(true)}
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Defaults
              </Button>
            ) : (
              <Button variant="ghost" asChild>
                <Link to="/continuity">Cancel</Link>
              </Button>
            )}
            
            {/* Right side: Save button */}
            <Button 
              onClick={handleSave} 
              disabled={saving || (isFormConfigured && !confirmed)} 
              className="px-8"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {isFormConfigured ? (isCurrentlyConfigured ? 'Save Changes' : 'Activate Continuity') : 'Save & Deactivate'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reset Confirmation Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-amber-600" />
              Reset to Defaults?
            </DialogTitle>
            <DialogDescription className="pt-2 space-y-3">
              <p>
                This will clear all continuity settings:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Check-in frequency will be set to 0</li>
                <li>Email and SMS options will be unchecked</li>
                <li>You will need to click <strong>Save</strong> to apply</li>
              </ul>
              <p className="text-amber-600 font-medium">
                After saving, continuity will be deactivated.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowResetDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="default"
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleResetToDefaults}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Info Notice */}
      <div className="flex gap-3 p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800 dark:text-blue-200">
          <p className="font-medium">How check-ins work</p>
          <p className="mt-1">
            You&apos;ll receive a reminder with a unique link. Click it, log in, or use the &quot;Check In&quot; button 
            on your dashboard to confirm. Missing check-ins triggers the escalation steps above.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ContinuitySetup;
